<template>
    <b-modal
      v-model="data.showModal"
      size="xxl"
      button-size="sm"
      modal-class="dropdown-options-editor"
      title-class="w-full"
      footer-class="w-full"
      no-fade
      @show="onShow"
      no-close-on-esc
      no-close-on-backdrop
      scrollable
    >
      <!-- Modal header -->
      <div slot="modal-title">
        <div class="flex w-full justify-between">
          <div class="flex gap-1">
            <div>Edit options</div>
            <div v-if="group">- {{ group.group_name }}</div>
          </div>
        </div>
      </div>

      <!-- Modal content -->
      <div v-if="group && groupOptions" class="flex flex-col gap-4">
        <div class="max-w-xs">
          <b-form-input
            v-model="searchText"
            size="sm"
            type="search"
            trim
            placeholder="Search in all fields"
            autofocus
            autocomplete="off"
          >
          </b-form-input>
        </div>

        <div class="flex flex-col">
          <table
            class="table table-google table-highlight"
          >
            <thead class="sticky z-10" style="top: -1rem;">
              <tr>
                <th v-if="isManual"><!-- drag handle --></th>
                <th>Option</th>
                <th class="text-nowrap">
                  Note
                </th>
                <th v-if="shortRequired" class="text-nowrap">
                  Short
                </th>
                <th
                  v-for="extra in group.extras"
                  :key="`extra_${extra.id}_th`"
                  class="row-fit text-nowrap"
                >
                  {{ extra.extra_title }}
                </th>
                <th><!-- Actions --></th>
                <th><!-- Filler --></th>
              </tr>
            </thead>

            <draggable
              tag="tbody"
              :list="groupOptions"
              handle=".drag-handle"
            >
              <tr v-for="option in filteredGroupOptions" :key="option.guid">
                <td
                  v-if="isManual"
                  class="drag-handle row-fit"
                  :class="{ 'invisible': !showDragHandle }"
                >
                  <div class="flex justify-center">
                    <i class="fas fa-bars"></i>
                  </div>
                </td>

                <td>
                  <div>
                    <b-input-group
                      :prepend="group.option_prefix"
                      size="sm"
                      class="flex-nowrap"
                    >
                      <b-form-input
                        class="field"
                        :style="getFieldStyle('option_value')"
                        v-model="option.option_value"
                        placeholder="Value"
                        size="sm"
                        trim
                        autocomplete="off"
                      ></b-form-input>

                      <b-input-group-append>
                        <b-input-group-text v-if="group.option_suffix">
                          {{ group.option_suffix }}
                        </b-input-group-text>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                </td>

                <td>
                  <b-form-input
                    v-model="option.option_note"
                    class="field"
                    :style="getFieldStyle('option_note')"
                    placeholder="Note"
                    size="sm"
                    trim
                    autocomplete="off"
                  ></b-form-input>
                </td>
                <td v-if="shortRequired">
                  <b-form-input
                    v-model="option.option_short"
                    class="field"
                    :style="getFieldStyle('option_short')"
                    placeholder="Short"
                    size="sm"
                    trim
                    autocomplete="off"
                    :class="{ 'border-danger': shortDuplicates.includes(option.option_short) }"
                  ></b-form-input>
                </td>

                <th
                  v-for="extra in group.extras"
                  :key="`extra_${extra.id}_td`"
                  class="row-fit text-nowrap"
                >
                  <div v-if="extra.dropdown_key === null">
                     <b-form-input
                        v-model="option[extra.extra_key]"
                        class="field"
                        :style="getFieldStyle(extra.extra_key)"
                        placeholder="Value"
                        size="sm"
                        trim
                        autocomplete="off"
                      ></b-form-input>
                  </div>
                  <div v-else>
                      <v-select
                        v-model="option[extra.extra_key]"
                        :options="dropdowns[extra.dropdown_key]"
                        size="sm"
                        label="text"
                        :reduce="option => option.text"
                        class="field"
                        :style="getFieldStyle(extra.extra_key)"
                        appendToBody
                      />
                  </div>
                </th>

                <td class="row-fit">
                  <b-btn
                    size="sm"
                    variant="danger"
                    :disabled="isLoading"
                    @click="deleteOption(option)"
                    title="Delete option"
                  >
                    &times;
                  </b-btn>
                </td>

                <td class="w-100"><!-- Filler --></td>
              </tr>
            </draggable>
          </table>
        </div>
      </div>

      <!-- Modal footer -->
      <div slot="modal-footer" class="w-full d-flex gap-4 justify-content-between items-center">
        <div class="flex w-full justify-content-between items-center">
          <div class="flex gap-4">
            <b-btn
              size="sm"
              variant="primary"
              :disabled="isLoading || !canSave"
              @click="onSave"
            >
              Save
            </b-btn>

            <b-btn
              size="sm"
              variant="primary"
              :disabled="isLoading"
              @click="createOption"
            >
              Add new option
            </b-btn>
          </div>
          <div class="flex gap-8 items-center">
            <div v-if="shortDuplicates.length > 1">
              <span>Duplicates: </span>
              <span class="text-red-500 font-bold">{{ shortDuplicates.join(', ') }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </template>

<script>
import {
  calculateDropdownPosition,
  generateGuid,
  getArrayDuplicates,
} from '@/helpers';
import draggable from 'vuedraggable';

export default {
  name: 'SelectEditor',
  components: {
    draggable,
  },
  props: {
    data: Object,
  },
  computed: {
    filteredGroupOptions() {
      if (this.searchText.length === 0 || this.group === null) {
        return this.groupOptions;
      }
      const searchText = this.searchText.toLowerCase();
      return this.groupOptions.filter((option) => {
        const valueMatch = option.option_value.toLowerCase().includes(searchText);
        if (valueMatch) {
          return true;
        }
        const noteMatch = option.option_note.toLowerCase().includes(searchText);
        if (noteMatch) {
          return true;
        }
        const shortMatch = option.option_short.toLowerCase().includes(searchText);
        if (shortMatch) {
          return true;
        }
        const extraMatch = this.group.extras.some((extra) => {
          const value = option[extra.extra_key];
          if (value === null) {
            return false;
          }
          return value.toLowerCase().includes(searchText);
        });
        if (extraMatch) {
          return true;
        }
        return false;
      });
    },
    dropdowns() {
      return this.$store.state.app.dropdowns;
    },
    shortRequired() {
      return this.group?.require_short === true;
    },
    shortDuplicates() {
      if (!this.groupOptions) {
        return [];
      }
      const shorts = this.groupOptions.map((option) => option.option_short);
      if (shorts === undefined) return [];
      const duplicates = getArrayDuplicates(shorts);
      return duplicates;
    },
    isLoading() {
      return this.loadingCount > 0;
    },
    isManual() {
      return this.group?.option_sort === false;
    },
    showDragHandle() {
      return this.isManual && this.searchText.length === 0;
    },
    canSave() {
      if (!this.groupOptions) return false;
      if (this.shortDuplicates.length > 1) return false;
      return true;
    },
    sendGroupOptions() {
      if (!this.groupOptions) {
        return null;
      }
      const sendGroupOptions = this.groupOptions.map((option, index) => ({
        ...option,
        option_index: index + 1,
      }));
      return sendGroupOptions;
    },
  },
  data() {
    return {
      searchText: '',
      loadingCount: 0,
      options: {
        tab: [
          { text: 'Base', value: 'base' },
          { text: 'Extras', value: 'extras' },
        ],
      },
      defaultFieldWidth: '12.5rem',
      fieldWidths: {},
      group: null,
      groupOptions: null,
      canvas: null,
    };
  },
  methods: {
    getFieldStyle(optionKey) {
      const fieldStyle = {
        width: this.fieldWidths[optionKey] ?? this.defaultFieldWidth,
      };
      return fieldStyle;
    },
    calculateTextWidth(text) {
      const canvas = this.canvas || (this.canvas = document.createElement('canvas'));
      const context = canvas.getContext('2d');
      context.font = '0.75rem ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';
      const metrics = context.measureText(text);
      return metrics.width;
    },
    calculateFieldWidth(options, title, extraWidth = 45) {
      const longestValue = options.reduce((a, b) => (a.length > b.length ? a : b), '');
      if (longestValue.length === 0) {
        return this.defaultFieldWidth;
      }
      const TITLE_SIZE_MULTIPLIER = 1.22;
      const FIELD_WHITESPACE_MULTIPLIER = 1.15;
      const minWidth = TITLE_SIZE_MULTIPLIER * this.calculateTextWidth(title);
      const maxWidth = Math.max(...options.map((option) => (this.calculateTextWidth(option))));
      // extraWidth = helps smaller fields and accounts for padding with border around input
      const widthPx = Math.max(minWidth, (maxWidth * FIELD_WHITESPACE_MULTIPLIER) + extraWidth);
      const widthRem = widthPx / 16;
      return `${widthRem}rem`;
    },
    calculateFieldWidths() {
      if (this.group === null) {
        return;
      }
      if (this.groupOptions === null) {
        return;
      }
      const baseFields = [
        { key: 'option_value', title: 'Option' },
        { key: 'option_note', title: 'Note' },
        { key: 'option_short', title: 'Short' },
      ];
      baseFields.forEach((baseField) => {
        const options = this.groupOptions
          .map((option) => option[baseField.key])
          .filter((text) => text.length > 0);
        this.fieldWidths[baseField.key] = this.calculateFieldWidth(options, baseField.title);
      });

      this.group.extras.forEach((extra) => {
        if (extra.dropdown_key === null) {
          const options = this.groupOptions
            .map((option) => option[extra.extra_key])
            .filter((text) => text.length > 0);
          this.fieldWidths[extra.extra_key] = this.calculateFieldWidth(options, extra.extra_title);
        } else {
          const options = this.dropdowns[extra.dropdown_key].map((option) => option.text);
          this.fieldWidths[extra.extra_key] = this.calculateFieldWidth(
            options,
            extra.extra_title,
            90,
          );
        }
      });
    },
    calculatePosition(dropdownList, component) {
      return calculateDropdownPosition(dropdownList, component);
    },
    fetchGroup() {
      this.loadingCount++;
      this.$http
        .get(`/select/groups/${this.data.groupKey}`)
        .then((res) => {
          this.group = res.body;
          this.calculateFieldWidths();
        })
        .catch((err) => {
          this.$error.toast(`Failed to fetch group: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchGroupOptions() {
      this.loadingCount++;
      this.$http
        .get(`/select/group_options/${this.data.groupKey}`)
        .then((res) => {
          res.body.forEach((option) => {
            option.guid = generateGuid();
          });
          this.groupOptions = res.body;
          this.calculateFieldWidths();
        })
        .catch((err) => {
          this.$error.toast(`Failed to fetch group: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    updateGroupOptions() {
      this.loadingCount++;
      this.$http
        .put(`/select/group_options/${this.data.groupKey}`)
        .send({ data: this.sendGroupOptions })
        .then(() => {
          this.$emit('updated');
          this.onClose();
        })
        .catch((err) => {
          this.$toast.error(`Failed to update group: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
          this.$store.dispatch('fetchDropdowns');
        });
    },
    createOption() {
      this.searchText = '';
      const newOption = {
        id: null,
        guid: generateGuid(),
        option_index: null,
        option_value: '',
        option_short: '',
        option_note: '',
      };
      this.group.extras.forEach((extra) => {
        newOption[extra.extra_key] = '';
      });
      this.groupOptions.push(newOption);
      this.$nextTick().then(() => {
        const inputField = document.querySelector('.modal.dropdown-options-editor .modal-body table tbody > tr:last-child input:first-child');
        if (inputField) {
          inputField.focus();
        }
      });
    },
    deleteOption(deleteOption) {
      if (deleteOption.option_value.length !== 0 && !confirm(`Do you really wish to delete '${deleteOption.option_value}' option?`)) {
        return;
      }
      this.groupOptions = this.groupOptions.filter((option) => option.guid !== deleteOption.guid);
    },
    fetchAll() {
      this.fetchGroup();
      this.fetchGroupOptions();
    },
    onSave() {
      this.updateGroupOptions();
    },
    onShow() {
      this.fetchAll();
    },
    onClose() {
      this.data.showModal = false;
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
</style>
